import {
  SUMMARY_ENTITY_LABELS,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import type { InputTaskCategory } from 'interfaces/IInputTask';
import type { SummaryEntityCategory } from '../../../../redux/slices/InsightsSlice';
import {
  ANALYSIS_COST_CATEGORIES,
  STEP_TYPE_TO_SUMMARY_ENTITY,
} from './computeAnalysis';
import { computeGrossMargin } from './computeGrossMargin';

export interface CategorySpendLine {
  category: SummaryEntityCategory;
  /** Display label from SUMMARY_ENTITY_LABELS. */
  label: string;
  /** Total spend in $ (positive). */
  totalSpend: number;
  /** Area-weighted cost per ha across all steps of this category (positive). */
  ratePerHa: number;
}

export interface CategorySpendResult {
  /** Rows in ANALYSIS_COST_CATEGORIES display order; only categories with spend > 0. */
  lines: CategorySpendLine[];
  /** False when no inputs have any cost data. */
  hasData: boolean;
}

/**
 * Aggregate per-category spend across all scoped inputs.
 *
 * ── Aggregation ───────────────────────────────────────────────────────────────
 * Calls computeGrossMargin for each input, restricted to `categories` (e.g.
 * Plan only, Actual only, or both — the Live Panel calls this once per
 * category so it can show Plan/Actual side by side without conflating them).
 * Maps each CostLine's stepType → SummaryEntityCategory via
 * STEP_TYPE_TO_SUMMARY_ENTITY (CropProtection + Adjuvant → Plant Protection).
 *
 * ── Rate ($/ha) ───────────────────────────────────────────────────────────────
 * Each CostLine carries totalCost (positive $) and costPerHa ($/ha).
 * Back-calculates totalArea = totalCost / costPerHa and accumulates both,
 * then divides at the end: ratePerHa = totalSpend / totalArea.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeCategorySpend = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): CategorySpendResult => {
  const acc: Partial<
    Record<SummaryEntityCategory, { cost: number; area: number }>
  > = {};

  for (const input of inputs) {
    const result = computeGrossMargin(input, categories, gUnits);
    if (!result.hasData) continue;

    for (const line of result.costBreakdown) {
      const cat = STEP_TYPE_TO_SUMMARY_ENTITY[line.stepType];
      if (!cat) continue;

      const existing = acc[cat] ?? { cost: 0, area: 0 };
      // Back-calculate the area this cost line covers
      const area = line.costPerHa > 0 ? line.totalCost / line.costPerHa : 0;
      acc[cat] = {
        cost: existing.cost + line.totalCost,
        area: existing.area + area,
      };
    }
  }

  const lines: CategorySpendLine[] = [];
  for (const cat of ANALYSIS_COST_CATEGORIES) {
    const entry = acc[cat];
    if (!entry || entry.cost === 0) continue;
    lines.push({
      category: cat,
      label: SUMMARY_ENTITY_LABELS[cat] ?? cat,
      totalSpend: entry.cost,
      ratePerHa: entry.area > 0 ? entry.cost / entry.area : 0,
    });
  }

  return {
    lines,
    hasData: lines.length > 0,
  };
};
